import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';
import { useAdEvent } from '../../hooks/useAdEvent';
import { AdResponse, AdPosition } from '../../types/Ad.api.type';
import { getAdIconPreset } from '../../constants/adIcons';

/** localStorage 키: ad_dismiss_{adId} */
const DISMISS_KEY_PREFIX = 'ad_dismiss_';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24시간

interface MiniBannerProps {
  ad: AdResponse;
  position: AdPosition;
}

/**
 * 설문 상세 미니배너 (댓글 위)
 * 닫기 → 24h localStorage 숨김
 */
const MiniBanner = ({ ad, position }: MiniBannerProps) => {
  /** Hooks */
  const navigate = useNavigate();
  const { impressionRef, handleClick, handleDismiss } = useAdEvent(ad.adId, position);
  const preset = getAdIconPreset(ad.iconType);
  const IconComp = preset.icon;

  /** States */
  const [isDismissed, setIsDismissed] = useState(() => {
    const dismissedAt = localStorage.getItem(`${DISMISS_KEY_PREFIX}${ad.adId}`);
    if (!dismissedAt) return false;
    const elapsed = Date.now() - Number(dismissedAt);
    if (elapsed >= DISMISS_DURATION_MS) {
      localStorage.removeItem(`${DISMISS_KEY_PREFIX}${ad.adId}`);
      return false;
    }
    return true;
  });

  /** Handlers */
  const handleBannerClick = useCallback(() => {
    handleClick();
    if (ad.adType === 'SURVEY' && ad.targetQuestionId) {
      navigate(`/question/info/${ad.targetQuestionId}`);
    } else if (ad.adType === 'EXTERNAL' && ad.targetUrl) {
      window.open(ad.targetUrl, '_blank', 'noopener,noreferrer');
    }
  }, [handleClick, ad, navigate]);

  const handleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    handleDismiss();
    localStorage.setItem(`${DISMISS_KEY_PREFIX}${ad.adId}`, String(Date.now()));
    setIsDismissed(true);
  }, [handleDismiss, ad.adId]);

  if (isDismissed) return null;

  return (
    <div ref={impressionRef} className="my-3">
      <div
        className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100 cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-colors"
        onClick={handleBannerClick}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {ad.iconType === 'custom' && ad.brandLogoUrl ? (
            <img src={ad.brandLogoUrl} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: preset.bgColor }}
            >
              <IconComp size={14} color={preset.iconColor} />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-800 truncate">{ad.title}</p>
            {ad.description && (
              <p className="text-xs text-gray-500 truncate">{ad.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          {/* 모바일: 화살표, PC: 닫기 */}
          <ArrowRight className="w-4 h-4 text-gray-400 md:hidden" />
          <button
            onClick={handleClose}
            className="hidden md:flex items-center justify-center w-5 h-5 rounded-full hover:bg-white/50 transition-colors"
            aria-label="닫기"
          >
            <X className="w-3 h-3 text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MiniBanner;
